import { OPPORTUNITY_SEARCH_TYPE } from "constants/app";
import { omit } from "lodash";

import {
  trackEBuyOpportunityByRequestId,
  trackFindATenderOpportunityByOcid,
  trackOpportunityByExternalId,
} from "../opportunitiesSlice";

export const PAGE_SIZE = 50;

const buildSamGovPayload = (cleanedFilters, pageNumber, sorting) => ({
  ...cleanedFilters,
  pageNumber,
  pageSize: PAGE_SIZE,
  sortOrder: sorting.direction,
  sortColumn: sorting.field,
});

const buildRequestIdSourcePayload = (cleanedFilters, pageNumber, sorting) => {
  const { status, ...rest } = cleanedFilters;
  return {
    ...rest,
    ...(status ? { status: Array.isArray(status) ? status : [status] } : {}),
    pageNumber,
    pageSize: PAGE_SIZE,
    sortOrder: sorting.direction,
    sortColumn: sorting.field,
  };
};

const renameOcdsDateFilters = (filters) => {
  const { postedFrom, postedTo, ...rest } = filters;
  return {
    ...rest,
    ...(postedFrom === undefined ? {} : { publishedFrom: postedFrom }),
    ...(postedTo === undefined ? {} : { publishedTo: postedTo }),
  };
};

/**
 * `active` is a SAM.gov-only filter — its request DTO is the only one that declares the
 * field. The shared initial `additionalFilters` seeds `{ active: true }`, so on first load
 * (before the saved filter loads) it leaks into eBuy/Find-a-Tender requests, whose backend
 * DTOs reject unknown properties with HTTP 400. Strip it for those sources. Applied via
 * mapFilters, so it covers both the search and summary requests (both consume cleanedFilters).
 */
const dropSamGovOnlyFilters = (filters) => omit(filters, "active");

/**
 * Per-source configuration for Opportunity Search.
 * Adding a new tab type requires only a new entry here — no if/else chains elsewhere.
 *
 * Shape:
 *   searchUrl   — POST endpoint for paginated search results
 *   summaryUrl  — POST endpoint for total count
 *   trackUrl    — GET endpoint for tracking (receives identifier string)
 *   identifierKey — field on the row object used as the track identifier
 *   buildPayload  — builds the POST body for search requests
 *   annotateItem  — transforms raw API items before storing (e.g. stamps type field)
 *   trackAction   — returns the Redux action to dispatch after a successful track
 *   mapFilters    — optional. Rewrites cleanedFilters before they reach the API
 *                   (search + summary). Use for per-source key renames.
 */
export const OPPORTUNITY_SOURCE_CONFIG = {
  [OPPORTUNITY_SEARCH_TYPE.SAM_GOV]: {
    searchUrl: "/nextgen/opportunitySearch/samgov",
    summaryUrl: "/nextgen/opportunitySearch/samgov/summary",
    trackUrl: (id) => `/nextgen/opportunitySearch/samgov/track?solNumber=${id}`,
    identifierKey: "externalId",
    buildPayload: buildSamGovPayload,
    annotateItem: (item) => item,
    trackAction: (identifier, opportunityId) =>
      trackOpportunityByExternalId({ externalId: identifier, opportunityId }),
  },
  [OPPORTUNITY_SEARCH_TYPE.EBUY]: {
    searchUrl: "/nextgen/opportunitySearch/ebuy",
    summaryUrl: "/nextgen/opportunitySearch/ebuy/summary",
    trackUrl: (id) => `/nextgen/opportunitySearch/ebuy/track?requestId=${id}`,
    identifierKey: "requestId",
    buildPayload: buildRequestIdSourcePayload,
    mapFilters: dropSamGovOnlyFilters,
    annotateItem: (item) => ({ ...item, type: OPPORTUNITY_SEARCH_TYPE.EBUY }),
    trackAction: (identifier, opportunityId) =>
      trackEBuyOpportunityByRequestId({ requestId: identifier, opportunityId }),
  },
  [OPPORTUNITY_SEARCH_TYPE.FIND_A_TENDER]: {
    searchUrl: "/nextgen/opportunitySearch/ocds",
    summaryUrl: "/nextgen/opportunitySearch/ocds/summary",
    trackUrl: (id) => `/nextgen/opportunitySearch/ocds/track/${id}`,
    identifierKey: "ocid",
    buildPayload: buildRequestIdSourcePayload,
    mapFilters: (filters) =>
      renameOcdsDateFilters(dropSamGovOnlyFilters(filters)),
    annotateItem: (item) => ({
      ...item,
      type: OPPORTUNITY_SEARCH_TYPE.FIND_A_TENDER,
    }),
    trackAction: (identifier, opportunityId) => {
      return trackFindATenderOpportunityByOcid({
        ocid: identifier,
        opportunityId,
      });
    },
  },
};

export const getSourceConfig = (type) =>
  OPPORTUNITY_SOURCE_CONFIG[type] ??
  OPPORTUNITY_SOURCE_CONFIG[OPPORTUNITY_SEARCH_TYPE.SAM_GOV];
